/**
 * SoleadLab 2.0 — Field Add Control (FL-2)
 *
 * Quick-add entry point for FIELD-kind share-link visitors: pick a type,
 * then every map tap places an indication of that type — the exact operator
 * quick-add flow (same uiStore arming, same MapCanvas click handler, same
 * creation core), so the two paths can never drift. Renders nothing for
 * anyone who isn't a field visitor.
 *
 * The backend stamps createdVia provenance server-side and ops-logs every
 * field create, so owner review/undo covers creations like edits.
 */

import { useRef, useState } from 'react';
import { useUIStore, useMapStore, INDICATION_TYPES, type IndicationType } from '../../stores';
import { useFieldCapabilities } from '../../hooks/useShareViewer';
import { getFieldActor, setFieldActor } from '../../utils/fieldActor';
import { extractExifData } from '../../utils/exif';
import { quickCreateIndication } from '../../utils/createIndication';
import { handleFieldQuickAddCreated } from '../../utils/fieldPhotoFlow';
import './FieldActionsPanel.css';

/** Finite, in-range, and not the null-island neighborhood (drone-side GPS
 *  voids surface as ~0,0 — the 2026-07 Yassif audit guard-gap lesson). */
function isPlausibleCoords(coords: [number, number]): boolean {
  const [lng, lat] = coords;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (Math.abs(lat) < 0.5 && Math.abs(lng) < 0.5) return false;
  return true;
}

/** One-shot device position; resolves null on denial/timeout/unsupported. */
function getDevicePosition(timeoutMs = 8000): Promise<[number, number] | null> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.longitude, pos.coords.latitude]),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}

export default function FieldAddControl() {
  const caps = useFieldCapabilities();
  const isAddMode = useUIStore((s) => s.isAddMode);
  const quickAddType = useUIStore((s) => s.quickAddIndicationType);
  const setAddMode = useUIStore((s) => s.setAddMode);
  const setQuickAddIndicationType = useUIStore((s) => s.setQuickAddIndicationType);
  const defaultOrientation = useUIStore((s) => s.defaultIndicationOrientation);
  const setDefaultIndicationOrientation = useUIStore((s) => s.setDefaultIndicationOrientation);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [actorDraft, setActorDraft] = useState('');
  // Storage writes don't re-render; bump after confirming the name.
  const [, setActorVersion] = useState(0);
  // FL-3a Flow C: photo-first placement (EXIF GPS → device GPS → manual tap).
  const [placing, setPlacing] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const addNotification = useUIStore((s) => s.addNotification);
  const setPendingFieldPhoto = useUIStore((s) => s.setPendingFieldPhoto);
  // Owner phone pass: while the Field update card is open (an indication is
  // selected) this control overlapped its buttons — hide it; the armed state
  // persists in uiStore and the control reappears when the card closes.
  const previewIndicationId = useMapStore((s) => s.previewIndicationId);

  if (!caps.canCreate || !caps.linkId) return null;
  if (previewIndicationId) return null;

  const linkId = caps.linkId;
  const actor = getFieldActor(linkId);
  const confirmActor = () => {
    if (!actorDraft.trim()) return;
    setFieldActor(linkId, actorDraft);
    setActorVersion((v) => v + 1);
  };
  const armed = isAddMode && quickAddType !== null;

  const disarm = () => {
    setAddMode(false);
    setPickerOpen(false);
    // FL-3a: a photo parked for the no-GPS fallback dies with the arming —
    // it must never silently attach to a mark created much later.
    if (useUIStore.getState().pendingFieldPhoto) {
      setPendingFieldPhoto(null);
      addNotification({
        type: 'info',
        title: 'Photo discarded',
        message: 'No mark was placed. Take or pick the photo again when you report the finding.',
      });
    }
  };

  const armWith = (type: IndicationType) => {
    // Same arming order as IndicationsTicket: type first, then add mode
    // (setAddMode(true) without a type preserves the quick type).
    setQuickAddIndicationType(type);
    setAddMode(true);
    setPickerOpen(false);
  };

  // FL-3a Flow C: photo picked while armed → place the mark at the photo's
  // location. GPS priority: EXIF from the ORIGINAL file (the downscale in the
  // attach step strips EXIF) → device position (live captures carry no EXIF
  // GPS through web inputs) → no-GPS fallback: stay armed with the photo
  // pending, so the next map tap creates the mark AND attaches it.
  const handleLocationPhoto = async (list: FileList | null) => {
    const file = list?.[0];
    if (!file || placing || !quickAddType) return;
    setPlacing(true);
    try {
      let coords: [number, number] | null = null;
      let fromDevice = false;
      try {
        const exif = await extractExifData(file);
        if (exif.coordinates && isPlausibleCoords(exif.coordinates)) {
          coords = exif.coordinates;
        }
      } catch {
        // EXIF parse failure — fall through to device position.
      }
      if (!coords) {
        const devicePos = await getDevicePosition();
        if (devicePos && isPlausibleCoords(devicePos)) {
          coords = devicePos;
          fromDevice = true;
        }
      }

      // The photo attaches to the mark either way — park it on the bus.
      setPendingFieldPhoto({ file, name: file.name });

      if (!coords) {
        addNotification({
          type: 'info',
          title: 'No location found',
          message: 'Tap the map to place the mark — the photo will attach to it.',
        });
        return; // still armed; MapCanvas's next tap finishes the job
      }

      const id = quickCreateIndication(coords, quickAddType);
      if (!id) {
        setPendingFieldPhoto(null);
        addNotification({ type: 'error', title: "Couldn't create the mark" });
        return;
      }
      useMapStore.getState().flyTo(coords);
      addNotification({
        type: 'success',
        title: `${INDICATION_TYPES[quickAddType]?.label ?? quickAddType} added${fromDevice ? ' at your location' : ''}`,
        duration: 2500,
      });
      handleFieldQuickAddCreated(id); // opens the card + attaches the photo
    } finally {
      setPlacing(false);
    }
  };

  if (armed) {
    return (
      <div className="field-add field-add--armed" data-testid="field-add-armed">
        <span className="field-add__hint">
          Tap the map to add: <strong>{INDICATION_TYPES[quickAddType!]?.label ?? quickAddType}</strong>
        </span>
        {/* FL-2.5: default orientation for new marks (same uiStore default
            the operator quick-add uses in buildManualIndication). */}
        <label className="field-add__orient">
          <input
            type="number"
            min={0}
            max={359}
            inputMode="numeric"
            value={defaultOrientation || 0}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setDefaultIndicationOrientation(Number.isFinite(v) ? ((v % 360) + 360) % 360 : 0);
            }}
            aria-label="Mark orientation in degrees"
            data-testid="field-add-orientation"
          />
          °
        </label>
        {/* FL-3a Flow C: photo-first placement. No `capture` attr — the OS
            picker offers both camera and gallery in one tap. */}
        <button
          type="button"
          className="field-actions__btn field-add__photo"
          disabled={placing}
          onClick={() => photoInputRef.current?.click()}
          data-testid="field-add-photo-location"
        >
          {placing ? 'Locating…' : '📷 Photo'}
        </button>
        <button
          type="button"
          className="field-actions__btn field-actions__btn--primary"
          onClick={disarm}
          data-testid="field-add-done"
        >
          Done
        </button>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          className="field-actions__hidden-input"
          onChange={(e) => {
            void handleLocationPhoto(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
    );
  }

  return (
    <div className="field-add" data-testid="field-add-control">
      {pickerOpen ? (
        actor ? (
          <div className="field-add__picker" data-testid="field-add-picker">
            <span className="field-add__hint">What did you find?</span>
            {Object.entries(INDICATION_TYPES).map(([type, config]) => (
              <button
                key={type}
                type="button"
                className="field-actions__btn field-add__type"
                onClick={() => armWith(type as IndicationType)}
                data-testid={`field-add-type-${type}`}
              >
                {config.label}
              </button>
            ))}
            <button
              type="button"
              className="field-actions__link"
              onClick={() => setPickerOpen(false)}
            >
              cancel
            </button>
          </div>
        ) : (
          // Same identity rule as every field write: name first.
          <div className="field-add__picker" data-testid="field-add-name-prompt">
            <span className="field-add__hint">Your name (shown on every change)</span>
            <input
              type="text"
              maxLength={60}
              placeholder="e.g. John Doe"
              value={actorDraft}
              onChange={(e) => setActorDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmActor(); }}
            />
            <button
              type="button"
              className="field-actions__btn field-actions__btn--primary"
              disabled={!actorDraft.trim()}
              onClick={confirmActor}
            >
              Continue
            </button>
          </div>
        )
      ) : (
        <button
          type="button"
          className="field-actions__btn field-actions__btn--primary field-add__open"
          onClick={() => setPickerOpen(true)}
          data-testid="field-add-open"
        >
          + Report finding
        </button>
      )}
    </div>
  );
}
